import { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ToastMessageContext } from 'common/components/ToastMessage';
import { IAnyType } from 'common/types/common';
import { useFormik } from 'formik';

import { getEstimatorCopy } from './copy';
import { estimatorInitialValues, getEstimatorSchema, getEstimatorStepFields } from './schema';
import {
  IEstimatorApiResponse,
  IEstimatorChangesetPayload,
  IEstimatorFormValues,
  IEstimatorOption,
} from './types';

const createEstimatorSessionId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `est-session-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

export const useProjectEstimator = (language: 'en' | 'hr') => {
  const { showMessage } = useContext(ToastMessageContext);
  const [clientSessionId] = useState(createEstimatorSessionId);
  const [currentStep, setCurrentStep] = useState(0);
  const [deliveryState, setDeliveryState] = useState<IEstimatorApiResponse['delivery'] | null>(
    null,
  );
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [report, setReport] = useState<IEstimatorApiResponse['report'] | null>(null);

  const copy = useMemo(() => getEstimatorCopy(language), [language]);
  const appEnvironment = process.env.NEXT_PUBLIC_APP_ENV || 'local';

  const validationSchema = useMemo(() => getEstimatorSchema(language), [language]);

  const persistChangeset = async (payload: IEstimatorChangesetPayload) => {
    try {
      await axios.post('/api/project-estimator/changeset', payload);
    } catch (_error) {
      // Changeset sync is best-effort and must never interrupt the estimator flow.
    }
  };

  const formik = useFormik<IEstimatorFormValues>({
    enableReinitialize: false,
    initialValues: { ...estimatorInitialValues, language },
    onSubmit: async values => {
      setIsGeneratingReport(true);
      try {
        const response = await axios.post<IEstimatorApiResponse>(
          '/api/project-estimator/report',
          {
            ...values,
            clientSessionId,
            environment: appEnvironment,
          },
        );
        setDeliveryState(response.data.delivery);
        setReport(response.data.report);
        if (typeof window !== 'undefined' && appEnvironment === 'production') {
          const win = window as Window & {
            dataLayer?: Array<Record<string, unknown>>;
            gtag?: (
              command: 'event',
              eventName: 'conversion',
              params: { currency: string; send_to: string; value: number },
            ) => void;
            lintrk?: (event: 'track', data: { conversion_id: number }) => void;
          };
          win.dataLayer = win.dataLayer || [];
          win.dataLayer.push({
            event: response.data.delivery.emailSent
              ? 'estimate_completed'
              : 'estimate_completed_email_failed',
            emailSent: response.data.delivery.emailSent,
          });
          if (response.data.delivery.emailSent && typeof win.gtag === 'function') {
            win.gtag('event', 'conversion', {
              send_to: 'AW-16678684649/J7HrCPj5mswZEOmPgpE-',
              value: 1.0,
              currency: 'EUR',
            });
          }
          if (response.data.delivery.emailSent && typeof win.lintrk === 'function') {
            win.lintrk('track', { conversion_id: 30078737 });
          }
        }
      } catch (error) {
        const errorMessage =
          axios.isAxiosError(error) && error.response?.data?.error
            ? error.response.data.error
            : copy.validation.submitError;

        showMessage({
          duration: 6000,
          message: errorMessage,
          type: 'error',
        });
      } finally {
        setIsGeneratingReport(false);
      }
    },
    validationSchema,
    validateOnChange: false,
  });

  const stepFields = useMemo(
    () => getEstimatorStepFields(formik.values.projectType?.value),
    [formik.values.projectType?.value],
  );

  useEffect(() => {
    if (formik.values.language !== language) {
      formik.setFieldValue('language', language, false);
    }
  }, [formik, language]);

  const scrollToFieldError = (field: keyof IEstimatorFormValues) => {
    if (typeof window === 'undefined') {
      return;
    }

    const selectors = [
      `#${field}`,
      `#dropdown-button-${field}`,
      `[data-estimator-field="${field}"]`,
    ];

    const target = selectors
      .map(selector => document.querySelector(selector))
      .find((element): element is Element => Boolean(element));

    if (!target) {
      return;
    }

    target.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };

  const validateStep = async () => {
    const fields = stepFields[currentStep] || [];

    const touchedFields = fields.reduce((acc, field) => ({ ...acc, [field]: true }), {});
    formik.setTouched({ ...formik.touched, ...touchedFields } as IAnyType);

    const errors = (await formik.validateForm()) as IAnyType;
    const firstInvalidField = fields.find(field => !!errors[field]);

    return {
      firstInvalidField,
      valid: !firstInvalidField,
    };
  };

  const goNext = async () => {
    const { firstInvalidField, valid } = await validateStep();
    if (!valid) {
      if (firstInvalidField) {
        scrollToFieldError(firstInvalidField);
      }
      return;
    }

    void persistChangeset({
      clientSessionId,
      completedStep: currentStep + 1,
      environment: appEnvironment,
      totalSteps: stepFields.length,
      values: formik.values,
    });

    setCurrentStep(step => Math.min(step + 1, stepFields.length - 1));
    setTimeout(() => {
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  };

  const goBack = () => {
    setCurrentStep(step => Math.max(step - 1, 0));
  };

  const selectProjectType = (projectType: IEstimatorOption) => {
    if (formik.values.projectType?.value === projectType.value) {
      return;
    }

    setCurrentStep(0);
    setDeliveryState(null);
    setReport(null);

    formik.resetForm({
      values: {
        ...estimatorInitialValues,
        language,
        projectType,
      },
    });
  };

  const toggleArrayValue = (field: keyof IEstimatorFormValues, value: string) => {
    const currentValue = (formik.values[field] as string[]) || [];
    const nextValue = currentValue.includes(value)
      ? currentValue.filter(item => item !== value)
      : [...currentValue, value];

    formik.setFieldValue(field, nextValue);
    if ((formik.errors as IAnyType)[field]) {
      formik.validateField(field as string);
    }
  };

  const fieldError = (field: keyof IEstimatorFormValues) =>
    (formik.touched as IAnyType)[field] ? (formik.errors as IAnyType)[field] : undefined;

  return {
    currentStep,
    deliveryState,
    fieldError,
    formik,
    goBack,
    goNext,
    isGeneratingReport,
    report,
    setCurrentStep,
    selectProjectType,
    toggleArrayValue,
    totalSteps: stepFields.length,
  };
};
